import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { getAuthHeader, parseResponse } from '../api/helpers';
import { pillBtn, filterInputStyle } from '../theme';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.btrix.in';

const TYPE_META = {
  appointment_confirmation: { label: 'Confirmation',  bg: 'rgba(34,197,94,0.15)',  text: '#4ade80',  icon: '✅' },
  appointment_reschedule:   { label: 'Reschedule',    bg: 'rgba(234,179,8,0.15)',  text: '#fbbf24',  icon: '📅' },
  appointment_cancellation: { label: 'Cancellation',  bg: 'rgba(239,68,68,0.15)',  text: '#f87171',  icon: '✕'  },
  manual:                   { label: 'Manual',        bg: 'rgba(14,165,233,0.15)', text: '#38bdf8',  icon: '📨' },
};

const STATUS_META = {
  sent:   { bg: 'rgba(34,197,94,0.15)',  text: '#4ade80' },
  failed: { bg: 'rgba(239,68,68,0.15)',  text: '#f87171' },
};

function TypeBadge({ type }) {
  const m = TYPE_META[type] || { label: type, bg: 'rgba(100,116,139,0.15)', text: '#94a3b8', icon: '•' };
  return <span style={{ background: m.bg, color: m.text, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>{m.icon} {m.label}</span>;
}

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.sent;
  return <span style={{ background: m.bg, color: m.text, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, textTransform: 'capitalize' }}>{status}</span>;
}

function SourceBadge({ sentBy }) {
  const isBot = sentBy === 'whatsapp-bot';
  return (
    <span style={{ background: isBot ? 'rgba(139,92,246,0.15)' : 'rgba(14,165,233,0.15)', color: isBot ? '#c084fc' : '#38bdf8', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
      {isBot ? '🤖 Bot' : '👤 Staff'}
    </span>
  );
}

export default function Notifications({ user }) {
  const isSuperAdmin = user?.role === 'super-admin';
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading]             = useState(false);
  const [hospitals, setHospitals]         = useState([]);
  const [sourceFilter, setSourceFilter]   = useState('all');
  const today = new Date().toISOString().split('T')[0];
  const [filterDate,     setFilterDate]     = useState(today);
  const [filterType,     setFilterType]     = useState('all');
  const [filterStatus,   setFilterStatus]   = useState('all');
  const [filterHospital, setFilterHospital] = useState(isSuperAdmin ? '' : user?.hospital_id);

  const fetchHospitals = useCallback(async () => {
    try { const headers = await getAuthHeader(); setHospitals(parseResponse(await axios.get(`${API_URL}/hospitals`, { headers })).hospitals || []); } catch {}
  }, []);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await getAuthHeader();
      const params  = {};
      if (filterDate)                      params.date        = filterDate;
      if (filterType   !== 'all')          params.type        = filterType;
      if (filterStatus !== 'all')          params.status      = filterStatus;
      if (isSuperAdmin && filterHospital)  params.hospital_id = filterHospital;
      else if (!isSuperAdmin)              params.hospital_id = user?.hospital_id;
      setNotifications(parseResponse(await axios.get(`${API_URL}/notifications`, { headers, params })).notifications || []);
    } catch {} finally { setLoading(false); }
  }, [filterDate, filterType, filterStatus, filterHospital, isSuperAdmin, user]);

  useEffect(() => { if (isSuperAdmin) fetchHospitals(); }, []); // eslint-disable-line
  useEffect(() => { fetchNotifications(); }, [filterDate, filterType, filterStatus, filterHospital]); // eslint-disable-line

  const displayed = notifications.filter(n => {
    if (sourceFilter === 'bot')   return n.sent_by === 'whatsapp-bot';
    if (sourceFilter === 'staff') return n.sent_by !== 'whatsapp-bot';
    return true;
  });

  const counts = {
    total:      notifications.length,
    sent:       notifications.filter(n => n.status === 'sent').length,
    failed:     notifications.filter(n => n.status === 'failed').length,
    bot:        notifications.filter(n => n.sent_by === 'whatsapp-bot').length,
    staff:      notifications.filter(n => n.sent_by !== 'whatsapp-bot').length,
    confirm:    notifications.filter(n => n.type === 'appointment_confirmation').length,
    reschedule: notifications.filter(n => n.type === 'appointment_reschedule').length,
    cancel:     notifications.filter(n => n.type === 'appointment_cancellation').length,
  };

  const STAT_ROWS = [
    [
      { label: 'Total',       value: counts.total,  grad: 'linear-gradient(135deg,#6366f1,#8b5cf6)' },
      { label: 'Delivered',   value: counts.sent,   grad: 'linear-gradient(135deg,#22c55e,#16a34a)' },
      { label: 'Failed',      value: counts.failed, grad: 'linear-gradient(135deg,#ef4444,#dc2626)' },
      { label: '🤖 Bot Sends', value: counts.bot,   grad: 'linear-gradient(135deg,#8b5cf6,#ec4899)' },
    ],
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)' }}>Notification Log</div>
        <div style={{ fontSize: 12, color: '#475569', marginTop: 4 }}>All WhatsApp communications — appointments + automated sends</div>
      </div>

      {/* Stats row */}
      <div className="grid-4" style={{ marginBottom: 14 }}>
        {STAT_ROWS[0].map((s, i) => (
          <div key={i} className="stat-card" style={{ position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: s.grad, borderRadius: '12px 12px 0 0' }} />
            <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1, marginBottom: 6, marginTop: 8 }}>{s.value}</div>
            <div style={{ fontSize: 12, color: '#475569' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Type breakdown */}
      <div className="grid-3" style={{ marginBottom: 20 }}>
        {[
          { label: '✅ Confirmations', value: counts.confirm,    color: '#4ade80' },
          { label: '📅 Reschedules',   value: counts.reschedule, color: '#fbbf24' },
          { label: '✕ Cancellations', value: counts.cancel,     color: '#f87171' },
        ].map((s, i) => (
          <div key={i} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>{s.label}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Source tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {[{ key: 'all', label: `All (${counts.total})` }, { key: 'bot', label: `🤖 Bot (${counts.bot})` }, { key: 'staff', label: `👤 Staff (${counts.staff})` }].map(f => (
          <button key={f.key} onClick={() => setSourceFilter(f.key)} style={pillBtn(sourceFilter === f.key)}>{f.label}</button>
        ))}
      </div>

      {/* Filters */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 16px', marginBottom: 16, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        {isSuperAdmin && (
          <select style={filterInputStyle} value={filterHospital} onChange={e => setFilterHospital(e.target.value)}>
            <option value="">All hospitals</option>
            {hospitals.map(h => <option key={h.hospital_id} value={h.hospital_id}>{h.name}</option>)}
          </select>
        )}
        <input type="date" style={{ ...filterInputStyle, colorScheme: 'dark' }} value={filterDate} onChange={e => setFilterDate(e.target.value)} />
        <select style={filterInputStyle} value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="all">All types</option>
          <option value="appointment_confirmation">Confirmation</option>
          <option value="appointment_reschedule">Reschedule</option>
          <option value="appointment_cancellation">Cancellation</option>
          <option value="manual">Manual</option>
        </select>
        <select style={filterInputStyle} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="all">All statuses</option>
          <option value="sent">Sent</option>
          <option value="failed">Failed</option>
        </select>
        <button onClick={fetchNotifications} style={{ padding: '8px 16px', borderRadius: 8, background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          Refresh
        </button>
      </div>

      {/* Table */}
      <div className="page-card">
        {loading ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#334155', fontSize: 13 }}>Loading notifications…</div>
        ) : displayed.length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#334155', fontSize: 13 }}>No notifications for selected filters</div>
        ) : (
          <div className="table-wrap">
            <table className="dark-table">
              <thead><tr>{['Time','Patient','Phone','Type','Source','Appointment ID','Status'].map(h => <th key={h}>{h}</th>)}</tr></thead>
              <tbody>
                {displayed.map((n, i) => (
                  <tr key={i}>
                    <td>
                      <div style={{ fontSize: 12, color: 'var(--text-primary)', fontWeight: 500 }}>{new Date(n.sent_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>
                      <div style={{ fontSize: 11, color: '#475569' }}>{new Date(n.sent_at).toLocaleDateString('en-IN')}</div>
                    </td>
                    <td style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{n.patient_name || '—'}</td>
                    <td style={{ fontSize: 12, color: '#64748b', fontFamily: 'monospace' }}>{n.patient_phone || '—'}</td>
                    <td><TypeBadge type={n.type} /></td>
                    <td><SourceBadge sentBy={n.sent_by} /></td>
                    <td style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--accent-light)' }}>{n.appointment_id || '—'}</td>
                    <td><StatusBadge status={n.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
